import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkflowTemplate } from '@couch/shared';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { MACHINES_KEY } from '../machines/MachineActions';
import { RECIPES_KEY } from '../RecipesPage';
import { ImportReportView } from './ImportReportView';

type Source = { kind: 'template'; template: WorkflowTemplate } | { kind: 'file'; name: string; workflow: Record<string, unknown> };

const sourceTitle = (s: Source) => (s.kind === 'template' ? s.template.title : s.name.replace(/\.json$/i, ''));
const MAX_SHOWN = 60;

function TemplateCard({ template, chosen, onChoose }: { template: WorkflowTemplate; chosen: boolean; onChoose: () => void }) {
  return (
    <li>
      <button type="button" className="template-card" aria-pressed={chosen} onClick={onChoose}>
        <img src={template.thumbnailUrl} alt="" loading="lazy" />
        <strong>{template.title}</strong>
        <span className="muted">{template.group}{template.openSource ? '' : ' · Comfy.org credits'}</span>
      </button>
    </li>
  );
}

async function readWorkflowFile(file: File): Promise<Record<string, unknown>> {
  const parsed: unknown = JSON.parse(await file.text());
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error('This file is not a ComfyUI workflow.');
  return parsed as Record<string, unknown>;
}

/** Checks the chosen workflow against the machine and saves it as a recipe. */
function ImportPanel({ source, machineId, onClose }: { source: Source; machineId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const body = (save: boolean) => ({
    machineId, save, title: sourceTitle(source).slice(0, 120), ...(source.kind === 'template' ? { template: source.template.name } : { workflow: source.workflow }),
  });
  const check = useQuery({ queryKey: ['import-check', machineId, source], queryFn: () => api.importWorkflow(body(false)), retry: false, staleTime: Infinity });
  const save = useMutation({
    mutationFn: () => api.importWorkflow(body(true)),
    onSuccess: async ({ recipe }) => { await queryClient.invalidateQueries({ queryKey: RECIPES_KEY }); if (recipe) await navigate(`/workflows/${recipe.id}`); },
  });
  return (
    <div className="panel import-panel">
      <h2>{sourceTitle(source)}</h2>
      {source.kind === 'template' && <p className="muted">{source.template.description}</p>}
      {check.isPending && <p role="status">Checking it against the machine…</p>}
      {check.isError && <p role="alert" className="form-error">{errorMessage(check.error)}</p>}
      {check.data && <ImportReportView report={check.data.report} machineId={machineId} />}
      {save.isError && <p role="alert" className="form-error">{errorMessage(save.error)}</p>}
      <p>
        <button type="button" disabled={!check.isSuccess || save.isPending} onClick={() => { save.mutate(); }}>Save as workflow</button>{' '}
        <button type="button" className="button-quiet" onClick={onClose}>Close</button>
      </p>
    </div>
  );
}

function TemplateBrowser({ templates, chosen, onChoose }: { templates: WorkflowTemplate[]; chosen: string | null; onChoose: (t: WorkflowTemplate) => void }) {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const groups = useMemo(() => [...new Set(templates.map((t) => t.group))], [templates]);
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = templates.filter((t) => (!group || t.group === group)
    && words.every((w) => [t.title, t.description, ...t.tags, ...t.models].join(' ').toLowerCase().includes(w)));
  return (
    <>
      <div className="template-tools">
        <label>Search <input type="search" value={query} placeholder="pose, inpaint, wan, upscale…" onChange={(e) => { setQuery(e.target.value); }} /></label>
        {groups.length > 1 && (
          <div className="template-groups" role="group" aria-label="Kind">
            {['', ...groups].map((g) => <button key={g || 'all'} type="button" className="button-quiet" aria-pressed={group === g} onClick={() => { setGroup(g); }}>{g || 'All'}</button>)}
          </div>
        )}
      </div>
      <p className="muted">{shown.length} workflows{shown.length > MAX_SHOWN ? ` — the first ${MAX_SHOWN} shown, search to narrow down` : ''}</p>
      <ul className="template-grid">
        {shown.slice(0, MAX_SHOWN).map((t) => <TemplateCard key={t.name} template={t} chosen={chosen === t.name} onChoose={() => { onChoose(t); }} />)}
      </ul>
    </>
  );
}

/** ComfyUI's ready-made workflows, or the user's own workflow file, as a recipe (doc 35). */
export function ImportPage() {
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  const online = (machines.data?.machines ?? []).filter((m) => m.online);
  const [machineOverride, setMachineOverride] = useState('');
  const machineId = machineOverride || online[0]?.id || '';
  const templates = useQuery({ queryKey: ['templates', machineId], queryFn: () => api.workflowTemplates(machineId), enabled: machineId !== '' });
  const [source, setSource] = useState<Source | null>(null);
  const [fileError, setFileError] = useState('');
  const onFile = async (file: File | undefined) => {
    setFileError('');
    if (!file) return;
    try {
      setSource({ kind: 'file', name: file.name, workflow: await readWorkflowFile(file) });
    } catch (error) {
      setFileError(error instanceof SyntaxError ? 'This file is not valid JSON.' : errorMessage(error));
    }
  };
  return (
    <section aria-labelledby="import-title">
      <p><Link to="/workflows/new">← New workflow</Link></p>
      <h1 id="import-title">From ComfyUI</h1>
      <p className="muted">Start from one of ComfyUI's own workflows — video, editing, upscaling, 3D, audio — or from a workflow file you saved in ComfyUI.</p>
      {machines.isSuccess && online.length === 0 && <p className="notice">No machine is online. Switch one on to see its workflows.</p>}
      <div className="template-tools">
        <label>Machine{' '}
          <select value={machineId} onChange={(e) => { setMachineOverride(e.target.value); setSource(null); }}>
            {online.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label>Workflow file <input type="file" accept=".json,application/json" disabled={!machineId} onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} /></label>
      </div>
      {fileError && <p role="alert" className="form-error">{fileError}</p>}
      {templates.isPending && machineId !== '' && <p role="status">Asking ComfyUI for its workflows…</p>}
      {templates.isError && <p role="alert" className="form-error">{errorMessage(templates.error)}</p>}
      {templates.data && <TemplateBrowser templates={templates.data.templates} chosen={source?.kind === 'template' ? source.template.name : null} onChoose={(t) => { setSource({ kind: 'template', template: t }); }} />}
      {source && <ImportPanel key={sourceTitle(source)} source={source} machineId={machineId} onClose={() => { setSource(null); }} />}
    </section>
  );
}
